import { differenceInDays, format } from "date-fns";
import React from "react";
import { useMemo, useState } from "react";

import { faCircleInfo, faExclamationCircle } from "@fortawesome/pro-regular-svg-icons";

import {
	calculateCanManuallySendReminders,
	frequencyToQuestionCount,
	selectReminderCadence,
} from "@performyard/engagement";
import {
	Alert,
	ButtonStandard,
	Chip,
	Icon,
	InputWrapper,
	LogoLoader,
	Text,
	Tooltip,
	toast,
} from "@performyard/py-components";

import CloseSurveyModal from "../components/CloseSurveyModal";
import RemindersDetailsBox from "../components/RemindersDetailsBox";
import { SettingLabel } from "../components/SettingLabel";
import SettingsSection from "../components/SettingsSection";

import { LargeClipboardAvatarsPencil } from "../../../../../../pages/Surveys/illustrations/LargeClipboardAvatarsPencil";

import { useCompanyEngagementSettings } from "../../../../../../api/hooks/defs/company";
import {
	useCurrentPulseMeta,
	useEngagementSurveyQuestions,
	useSendManualReminders,
} from "../../../../../../api/hooks/defs/engagement";
import { useFeatureFlagQuery } from "../../../../../../hooks/useFeatureFlag";

import { ErrorResponse } from "../../../../../../api/services/defs/internal-api/internal-base-request";

const DEFAULT_FREQUENCY = 28;

const CurrentPulseTab = () => {
	const [isCloseModalOpen, setIsCloseModalOpen] = useState(false);

	const { data: engagementSettings, refetch, isLoading } = useCompanyEngagementSettings();
	const { data: engagementSurveyQuestions, isLoading: isLoadingEngagementSurveyQuestions } =
		useEngagementSurveyQuestions();
	const { value: engagementSurveyRemindersCooldownDays } = useFeatureFlagQuery(
		"ff-config-engagement-manual-reminder-timeout-period",
		3
	);

	const { data: currentPulseMeta, isLoading: isLoadingCurrentPulseMeta } = useCurrentPulseMeta();
	const { mutate: sendManualReminders, isPending: isSendingManualReminders } = useSendManualReminders({
		onSuccess: () => {
			toast.success("Reminders sent successfully");
			refetch();
		},
		onError: (error) => {
			toast.error(
				(error instanceof ErrorResponse && error.data?.message) ||
					"Reminders could not be sent. Please try again."
			);
			refetch();
		},
	});

	const canManuallySendReminders = useMemo(() => {
		if (!engagementSettings?.engagement) {
			return { canSend: false, error: "No engagement settings found", availableOn: new Date() };
		}

		const lastSurveyDate = engagementSettings.engagement.last_survey_date;
		const surveyFrequency = engagementSettings.engagement.frequency;
		const lastSurveyReminderDate = engagementSettings.engagement.last_survey_reminder_date;
		const frequency = surveyFrequency || DEFAULT_FREQUENCY;

		if (!lastSurveyDate) {
			return { canSend: false, error: "No survey date found", availableOn: new Date() };
		}

		const reminderCadence = engagementSettings.engagement.send_reminder_emails
			? selectReminderCadence(frequency)
			: [];

		return calculateCanManuallySendReminders(
			lastSurveyDate,
			lastSurveyReminderDate || null,
			reminderCadence,
			engagementSurveyRemindersCooldownDays
		);
	}, [engagementSettings, engagementSurveyRemindersCooldownDays]);

	// Calculate KPI card data - MUST be called before any early returns
	const kpiData = useMemo(() => {
		if (!currentPulseMeta?.state || currentPulseMeta.state !== "active") {
			return null;
		}

		// Days Remaining
		const daysRemaining = currentPulseMeta.nextSurveyDate
			? differenceInDays(new Date(currentPulseMeta.nextSurveyDate), new Date())
			: 0;

		let totalQuestions = frequencyToQuestionCount(engagementSettings?.engagement?.frequency || DEFAULT_FREQUENCY);
		const types: ("Factor" | "Custom" | "eNPS" | "Feedback")[] = ["Factor"];

		if (engagementSurveyQuestions && engagementSettings?.engagement?.custom_questions_frequency) {
			totalQuestions += engagementSettings?.engagement?.custom_questions_include_all
				? engagementSurveyQuestions?.customQuestionsCount
				: 0;
			types.push("Custom");
		}

		// account for ask additional enps feedback question
		if (engagementSettings?.engagement?.ask_additional_enps_feedback_question) {
			totalQuestions += 1;
			types.push("eNPS");
		}

		// account for additional feedback question
		if (engagementSettings?.engagement?.additional_feedback_question_frequency) {
			totalQuestions += 1;
			types.push("Feedback");
		}

		// Question counts by type

		// Question type breakdown string
		const questionTypes = types.join(", ");

		// Participation Rate
		const participationRate =
			currentPulseMeta.totalParticipants > 0
				? Math.round((currentPulseMeta.participantsWhoAnswered / currentPulseMeta.totalParticipants) * 100)
				: 0;

		const questionsAnsweredRate =
			currentPulseMeta.totalQuestions > 0
				? Math.round((currentPulseMeta.totalAnsweredQuestions / currentPulseMeta.totalQuestions) * 100)
				: 0;

		return {
			daysRemaining,
			startDate: currentPulseMeta.startDate,
			closingDate: currentPulseMeta.nextSurveyDate,
			closedAt: currentPulseMeta.closedAt,
			totalQuestions,
			questionTypes,
			participationRate,
			questionsAnsweredRate,
			participantsWhoAnswered: currentPulseMeta.participantsWhoAnswered,
			totalParticipants: currentPulseMeta.totalParticipants,
			status: currentPulseMeta.status,
		};
	}, [currentPulseMeta, engagementSettings, engagementSurveyQuestions]);

	if (isLoading || isLoadingCurrentPulseMeta || isLoadingEngagementSurveyQuestions)
		return (
			<div className="flex h-[400px] w-full items-center justify-center">
				<LogoLoader textArray={[]} mainText="Loading Settings..." />
			</div>
		);

	if (!engagementSettings) throw new Error("No engagement settings found");

	const engagement = engagementSettings.engagement;
	const hasActiveSurvey = !!engagement.last_survey_date && engagement.current_survey_status !== "closed";
	const canSendReminders =
		canManuallySendReminders.canSend && !isSendingManualReminders && engagement.enabled && hasActiveSurvey;
	const canCloseSurvey = engagement.enabled && hasActiveSurvey;

	const handleSendReminders = () => {
		if (engagement.current_survey_status === "closed") {
			toast.info("No active pulse survey available.");
		} else {
			sendManualReminders({});
		}
	};

	const handleCloseSurvey = () => {
		setIsCloseModalOpen(true);
	};

	// Render empty states
	if (!isLoadingCurrentPulseMeta && !isLoading) {
		if (currentPulseMeta?.state === "disabled") {
			return (
				<div className="flex flex-col items-center justify-center px-4 py-16">
					<LargeClipboardAvatarsPencil />
					<div className="max-w-md text-center">
						<Text
							size="paragraph-2"
							weight="semi-bold"
							color="neutral-7"
							overrideElement="p"
							dangerouslySetClassName="mt-4"
						>
							Enable Pulse Surveys to See Your Current Pulse
						</Text>
					</div>
				</div>
			);
		} else if (engagement?.next_survey_date && !currentPulseMeta) {
			return (
				<div className="flex flex-col items-center justify-center px-4 py-16">
					<LargeClipboardAvatarsPencil />
					<div className="max-w-md text-center">
						<Text
							size="paragraph-2"
							weight="semi-bold"
							color="neutral-7"
							overrideElement="p"
							dangerouslySetClassName="mt-4"
						>
							Next Pulse Starting on: {format(new Date(engagement?.next_survey_date), "MMMM d, yyyy")}
						</Text>
					</div>
				</div>
			);
		}
	}

	return (
		<>
			{/* KPI Cards */}
			{!isLoadingCurrentPulseMeta && kpiData && (
				<div className="mb-8 mt-10 grid grid-cols-3 gap-6">
					{/* Days Remaining Card */}
					<div className="space-y-4 rounded-lg border border-solid border-neutral-3 bg-white p-4">
						<div className="flex items-start">
							<Text size="paragraph-3" weight="regular" overrideElement="h2" color="neutral-9">
								Days Remaining
							</Text>
							<div className="-mt-2 [&>button]:p-0">
								<Tooltip content="This number is a count of days remaining for pulse participants to respond.">
									<Icon icon={faCircleInfo} size={12} color="neutral-6" />
								</Tooltip>
							</div>
						</div>
						<div className="flex h-[48px] items-end justify-between gap-2">
							<div className="flex items-center gap-3">
								<p className="leading-12 text-[56px] font-black text-primary-9">
									{kpiData?.closedAt || kpiData?.status === "closed" ? 0 : kpiData.daysRemaining}
								</p>
								<Text size="caption" weight="semi-bold" color="primary-9">
									Days <br /> Remaining
								</Text>
							</div>
							<div className="space-y-1">
								{kpiData.closedAt || kpiData?.status === "closed" ? (
									<div className="flex items-center gap-2">
										<Chip
											size={20}
											color="neutral"
											label={
												kpiData.closedAt
													? `Closed on: ${format(new Date(kpiData.closedAt), "MMM d, yyyy")}`
													: `Closed`
											}
										/>
									</div>
								) : (
									<>
										<Text size="caption" weight="semi-bold" color="primary-9">
											Opened: {format(new Date(kpiData.startDate), "MMM d, yyyy")}
										</Text>
										<Text size="caption" weight="semi-bold" color="primary-9">
											Closing on:{" "}
											{kpiData.closingDate
												? format(new Date(kpiData.closingDate), "MMM d, yyyy")
												: "N/A"}
										</Text>
									</>
								)}
							</div>
						</div>
					</div>

					{/* Pulse Configuration Card */}
					<div className="space-y-4 rounded-lg border border-solid border-neutral-3 bg-white p-4">
						<div className="flex items-start">
							<Text size="paragraph-3" weight="regular" overrideElement="h2" color="neutral-9">
								Pulse Configuration
							</Text>
							<div className="-mt-2 [&>button]:p-0">
								<Tooltip content="This section provides a reminder of what your current pulse's question configuration was at the time the pulse survey was initiated.">
									<Icon icon={faCircleInfo} size={12} color="neutral-6" />
								</Tooltip>
							</div>
						</div>
						<div className="flex h-[48px] items-center justify-between gap-2">
							<div className="flex items-center gap-3">
								<p className="leading-12 text-[56px] font-black text-primary-9">
									{kpiData.totalQuestions}
								</p>
								<div className="max-w-[160px]">
									<Text size="caption" weight="semi-bold" color="primary-9">
										Questions ({kpiData.questionTypes})
									</Text>
								</div>
							</div>
						</div>
					</div>

					{/* Participation Rate Card */}
					<div className="space-y-4 rounded-lg border border-solid border-neutral-3 bg-white p-4">
						<div className="flex items-start">
							<Text size="paragraph-3" weight="regular" overrideElement="h2" color="neutral-9">
								Participation Rate
							</Text>
							<div className="-mt-2 [&>button]:p-0">
								<Tooltip content="This number is the participation rate of the current pulse survey.">
									<Icon icon={faCircleInfo} size={12} color="neutral-6" />
								</Tooltip>
							</div>
						</div>
						<div className="flex h-[48px] items-center justify-between gap-2">
							<div className="flex items-center gap-3">
								<p className="leading-12 text-[56px] font-black text-primary-9">
									{kpiData.participationRate}%
								</p>
							</div>
							<div className="space-y-1">
								<Text size="caption" weight="semi-bold" color="primary-9">
									Questions Answered: {kpiData.questionsAnsweredRate}%
								</Text>
								<Text size="caption" weight="semi-bold" color="primary-9">
									Participants: {kpiData.participantsWhoAnswered} ({kpiData.totalParticipants} Total)
								</Text>
							</div>
						</div>
					</div>
				</div>
			)}

			<SettingsSection
				title="Current Pulse Survey"
				description="Take actions on the current active pulse survey."
			>
				<div className="space-y-8">
					<div className="space-y-4">
						<InputWrapper
							labelPosition="left"
							justify="between"
							id="manual-reminders"
							label={
								<SettingLabel
									title="Manual Reminder"
									description="Send an email reminder to participants who have not responded to the current pulse survey."
									tooltip={`This option has a ${engagementSurveyRemindersCooldownDays} business day reset timer that will be applied after use`}
								/>
							}
						>
							<div className="flex flex-col items-end gap-2">
								<div className="w-fit">
									<ButtonStandard
										onClick={handleSendReminders}
										disabled={!canSendReminders}
										data-testid="send-reminder-button"
									>
										Send Reminder
									</ButtonStandard>
								</div>
								{!canManuallySendReminders?.canSend && (
									<div className="flex items-center gap-1">
										<Alert
											icon={faExclamationCircle}
											message={`Will be available on ${format(canManuallySendReminders?.availableOn, "MM/dd/yyyy hh:mm a")}`}
											type="error"
											items="start"
										/>
									</div>
								)}
							</div>
						</InputWrapper>

						{engagement.frequency &&
							currentPulseMeta?.state === "active" &&
							currentPulseMeta?.status === "open" && (
								<RemindersDetailsBox
									frequency={engagement.frequency}
									nextDate={new Date(currentPulseMeta.startDate)}
									reminderEmailsSent={engagementSettings?.engagement?.reminder_emails_sent}
									sendReminderEmails={engagement.send_reminder_emails}
								/>
							)}
					</div>

					<InputWrapper
						labelPosition="left"
						justify="between"
						id="close-pulse"
						label={
							<SettingLabel
								title="Close Current Pulse"
								description="Stop capturing responses for the current pulse survey."
							/>
						}
					>
						<div className="w-fit">
							<ButtonStandard
								onClick={handleCloseSurvey}
								disabled={!canCloseSurvey}
								data-testid="close-pulse-button"
							>
								Close Pulse
							</ButtonStandard>
						</div>
					</InputWrapper>
				</div>
			</SettingsSection>

			<CloseSurveyModal
				isOpen={isCloseModalOpen}
				handleClose={() => setIsCloseModalOpen(false)}
				triggerRefetch={refetch}
			/>
		</>
	);
};

export default CurrentPulseTab;
